import classNames from "classnames";
import React from "react";
import { IFRAME_SANDBOX } from "../../build/iframeConstants";
import { IframeNode, NodeStates } from "../../build/types";
import { BuilderComponentProps } from "../BuilderInterface";
import { getCompoundStyle, getStateStyleForBuilder, getStylesFromInteractiveStates } from "../helpers";
import "./style.scss";
import { useInteractionStates } from "../../hooks/useInteractionStates";

export interface BuilderIframeProps extends BuilderComponentProps {
    /** When set, render a slot div for a preloaded iframe instead of a new iframe (used by Render for offline reuse) */
    iframeSlotRef?: React.RefObject<HTMLDivElement>;
}

const BuilderIframe: React.FC<BuilderIframeProps> = ({
    node,
    actionCallbacks,
    children,
    builderProps,
    disabled,
    iframeSlotRef
}: BuilderIframeProps) => {
    const iNode = node as IframeNode;
    const { id, attr, stateManager, iframeUrl, isOffline, image } = iNode;
    const { setElement, states } = useInteractionStates<HTMLDivElement>();

    const iframeClasses = classNames("builder-iframe", {
        [`${attr?.className}`]: !!attr?.className
    });

    if (builderProps) {
        const { isContainer, nodeState, selectedMenu, extraChild, selectCallback, outlineCallback, onDragStart, onDragEnd } =
            builderProps;

        return (
            <div
                {...attr}
                className={iframeClasses}
                style={getCompoundStyle({
                    node,
                    isContainer,
                    stateStyle: getStateStyleForBuilder(stateManager, nodeState, "attr_style")
                })}
                onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                id={id}
                onDragStart={e => onDragStart?.(e, id)}
                draggable={"true"}
                onDragEnd={onDragEnd}
            >
                {iframeUrl && (
                    <>
                        <iframe src={iframeUrl} sandbox={IFRAME_SANDBOX} style={{ pointerEvents: "none" }} />
                        <div className="iframe-overlay-message">
                            <span>preview only</span>
                        </div>
                    </>
                )}
                {selectedMenu}
                {!iNode.iframeUrl && extraChild}
            </div>
        );
    } else {
        const iframeWrapperStyles = getStylesFromInteractiveStates({
            states,
            defaultStyle: getCompoundStyle({ node }),
            stateManager,
            paths: ["attr_style"],
            ...(disabled ? { extraStates: [NodeStates.DISABLED] } : {})
        });

        const usePreloadedSlot = Boolean(iframeSlotRef && iframeUrl && !isOffline);

        return (
            <div {...attr} {...actionCallbacks} className={iframeClasses} ref={setElement} style={iframeWrapperStyles}>
                {children}
                {usePreloadedSlot ? (
                    <div
                        ref={iframeSlotRef}
                        style={{ width: "100%", height: "100%", minHeight: 0 }}
                    />
                ) : isOffline ? (
                    <img src={image?.url || ""} alt="Fallback" />
                ) : (
                    <iframe src={iframeUrl} allowFullScreen={false} sandbox={IFRAME_SANDBOX} />
                )}
            </div>
        );
    }
};

export default BuilderIframe;
